import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { MapPin, Calendar, ChevronDown } from "lucide-react";
import { AskAboutButton } from "./AskAboutButton";

// Chronological order: oldest → newest (scroll forward in time)
const JOBS = [
  {
    id: 1,
    role: "Développeur Stagiaire",
    company: "StartupFactory",
    location: "Paris · Présentiel",
    period: "2018 — 2019",
    year: 2018,
    type: "Stage",
    color: "#86efac",
    tasks: [
      "Création d'un MVP d'application de gestion de tâches (React, Firebase)",
      "Tests unitaires et d'intégration avec Jest et Cypress",
      "Premiers pas en méthodologie Agile et en travail d'équipe produit",
    ],
    context: "Mon tout premier poste. Là où tout a commencé.",
  },
  {
    id: 2,
    role: "Développeur Web Junior",
    company: "ESN Consulting",
    location: "Lyon · Présentiel",
    period: "2019 — 2021",
    year: 2019,
    type: "CDI",
    color: "#f97316",
    tasks: [
      "Développement de fonctionnalités sur une application ERP interne (Vue.js, Laravel)",
      "Maintenance et migration de bases de données MySQL vers PostgreSQL",
      "Participation aux cérémonies Agile — sprint planning, daily, rétrospectives",
    ],
    context: "La montée en compétences. Beaucoup d'apprentissages sur le terrain.",
  },
  {
    id: 3,
    role: "Développeur Front-End",
    company: "Agence Digital Studio",
    location: "Paris · Hybride",
    period: "2021 — 2023",
    year: 2021,
    type: "CDI",
    color: "#a78bfa",
    tasks: [
      "Développement de 15+ sites e-commerce sur mesure (Next.js, Shopify, Headless CMS)",
      "Intégration d'animations complexes avec GSAP et Three.js",
      "Collaboration design/dev pour l'implémentation des design systems",
      "Réduction du time-to-interactive moyen de 4,2s à 1,8s",
    ],
    context: "Le tournant créatif. UX, animations et performance deviennent ma passion.",
  },
  {
    id: 4,
    role: "Développeur Full-Stack Senior",
    company: "TechVenture SAS",
    location: "Paris · Remote",
    period: "2023 — Présent",
    year: 2023,
    type: "CDI",
    color: "#22d3ee",
    tasks: [
      "Architecture et développement d'une plateforme SaaS B2B (React, Node.js, PostgreSQL)",
      "Mise en place d'une pipeline CI/CD réduisant les déploiements de 3h à 12 min",
      "Mentorat de 2 développeurs juniors et code reviews hebdomadaires",
      "Optimisation des performances → score Lighthouse passé de 62 à 96",
    ],
    context: "Aujourd'hui. Senior, autonome, et toujours aussi passionné.",
    isEducation: false,
  },
];

export function Experience({ experiences = [], education = [], language = "fr" }: { experiences?: any[], education?: any[], language?: string }) {
  const mappedExperiences = experiences.map((exp: any, index: number) => {
    const startYear = new Date(exp.start_date).getFullYear();
    const endYear = exp.end_date ? new Date(exp.end_date).getFullYear() : (language === "en" ? "Present" : "Présent");
    return {
      id: `exp-${exp.id || index}`,
      role: exp.position,
      company: exp.company,
      location: exp.location,
      period: `${startYear} — ${endYear}`,
      year: startYear,
      type: "Experience",
      color: JOBS[index % JOBS.length].color,
      tasks: exp.short_description ? exp.short_description.split('\n').filter(Boolean) : [],
      context: exp.objectif_but || "",
      isEducation: false,
    };
  });

  const mappedEducation = education.map((edu: any, index: number) => {
    const startYear = new Date(edu.start_date).getFullYear();
    const endYear = edu.end_date ? new Date(edu.end_date).getFullYear() : (language === "en" ? "Present" : "Présent");
    return {
      id: `edu-${edu.id || index}`,
      role: edu.degree,
      company: edu.school || edu.institution,
      location: edu.speciality || "Formation", // Use speciality or generic label
      period: `${startYear} — ${endYear}`,
      year: startYear,
      type: "Education",
      color: "#a78bfa", // Purple distinct color for education
      tasks: edu.description ? edu.description.split('\n').filter(Boolean) : [],
      context: language === "en" ? "Academic path" : "Parcours académique",
      isEducation: true,
    };
  });

  const displayJobs = [...mappedExperiences, ...mappedEducation].sort((a: any, b: any) => a.year - b.year);
  const finalDisplayJobs = displayJobs.length > 0 ? displayJobs : JOBS;

  const START_YEAR = finalDisplayJobs.length > 0 ? Math.min(...finalDisplayJobs.map((j: any) => j.year)) : 2018;
  const END_YEAR = new Date().getFullYear();

  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState(0);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    const onScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const sectionTop = window.scrollY + rect.top;
      const scrollable = section.offsetHeight - window.innerHeight;
      const scrolled = window.scrollY - sectionTop;
      const p = Math.max(0, Math.min(1, scrolled / scrollable));
      setProgress(p);
      const idx = Math.min(finalDisplayJobs.length - 1, Math.floor(p * finalDisplayJobs.length));
      setActiveIdx((prev) => {
        if (prev !== idx) setPrevIdx(prev);
        return idx;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [finalDisplayJobs.length]);

  const currentJob = finalDisplayJobs[activeIdx] || finalDisplayJobs[0];
  const displayYear = Math.round(START_YEAR + progress * (END_YEAR - START_YEAR));
  const direction = activeIdx >= prevIdx ? 1 : -1;

  return (
    <section
      id="experience"
      ref={sectionRef}
      style={{ height: `${finalDisplayJobs.length * 100 + 120}vh`, background: "#07070b", position: "relative" }}
    >
      {/* Sticky viewport */}
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Background year */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{ zIndex: 0 }}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={displayYear}
              initial={{ opacity: 0, scale: 1.15 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.4 }}
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "clamp(120px, 25vw, 320px)",
                fontWeight: 700,
                color: "rgba(255,255,255,0.025)",
                lineHeight: 1,
                letterSpacing: "-0.05em",
                userSelect: "none",
              }}
            >
              {displayYear}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Radial glow following active job color */}
        <motion.div
          animate={{ background: `radial-gradient(ellipse 60% 60% at 50% 50%, ${currentJob.color}06 0%, transparent 70%)` }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 0 }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
          {/* Section tag */}
          <div className="flex items-center gap-3 mb-6">
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "11px",
                color: "#22d3ee",
                letterSpacing: "0.15em",
              }}
            >
              {language === "en" ? "03 // EXPERIENCE" : "03 // EXPÉRIENCES"}
            </span>
            <div className="flex-1 h-px" style={{ background: "rgba(34,211,238,0.15)" }} />
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "11px",
                color: "rgba(255,255,255,0.25)",
                letterSpacing: "0.1em",
              }}
            >
              {activeIdx + 1} / {finalDisplayJobs.length}
            </span>
          </div>

          {/* Main content area */}
          <div className="flex-1 flex flex-col md:flex-row gap-8 md:gap-16 items-center min-h-0">
            {/* Left — vertical timeline dots */}
            <div className="hidden md:flex flex-col items-center gap-0 flex-shrink-0">
              {finalDisplayJobs.map((job: any, i: number) => {
                const isPast = i < activeIdx;
                const isActive = i === activeIdx;
                return (
                  <div key={job.id} className="flex flex-col items-center">
                    {/* Dot */}
                    <motion.div
                      animate={{
                        scale: isActive ? 1.3 : 1,
                        background: isActive ? job.color : isPast ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)",
                        boxShadow: isActive ? `0 0 12px ${job.color}80` : "none",
                      }}
                      transition={{ duration: 0.4 }}
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        border: `2px solid ${isActive ? job.color : isPast ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)"}`,
                        flexShrink: 0,
                      }}
                    />
                    {/* Line connector */}
                    {i < finalDisplayJobs.length - 1 && (
                      <div style={{ width: "2px", height: "60px", position: "relative", overflow: "hidden" }}>
                        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.06)" }} />
                        <motion.div
                          animate={{ height: isPast ? "100%" : isActive ? "50%" : "0%" }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            background: `linear-gradient(180deg, ${job.color}, ${finalDisplayJobs[i + 1].color})`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Center — main job card */}
            <div className="flex-1 relative min-h-0 flex items-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIdx}
                  initial={{ opacity: 0, y: direction > 0 ? 50 : -50, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: direction > 0 ? -30 : 30, filter: "blur(4px)" }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full"
                >
                  {/* Context */}
                  <div
                    className="mb-4 italic"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "13px",
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    // {currentJob.context}
                  </div>

                  {/* Role */}
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h2
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "clamp(24px, 3.5vw, 44px)",
                        fontWeight: 700,
                        color: "#ffffff",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.1,
                      }}
                    >
                      {currentJob.role}
                    </h2>
                    <motion.span
                      animate={{ background: `${currentJob.color}18`, borderColor: `${currentJob.color}40`, color: currentJob.color }}
                      className="px-2.5 py-0.5 rounded-full text-xs"
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        border: "1px solid",
                        fontSize: "10px",
                        letterSpacing: "0.08em",
                        flexShrink: 0,
                      }}
                    >
                      {currentJob.type}
                    </motion.span>
                  </div>

                  {/* Company */}
                  <motion.div
                    animate={{ color: currentJob.color }}
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "20px",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                  >
                    {currentJob.isEducation ? "🎓" : "@"} {currentJob.company}
                  </motion.div>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 mb-8">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={12} color="rgba(255,255,255,0.3)" />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: "rgba(255,255,255,0.35)" }}>
                        {currentJob.location}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} color="rgba(255,255,255,0.3)" />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: "rgba(255,255,255,0.35)" }}>
                        {currentJob.period}
                      </span>
                    </div>
                  </div>

                  {/* Tasks */}
                  <div className="space-y-3 max-w-2xl">
                    {currentJob.tasks?.map((task: string, ti: number) => (
                      <motion.div
                        key={ti}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: ti * 0.08, duration: 0.4 }}
                        className="flex items-start gap-3"
                      >
                        <motion.span
                          animate={{ background: currentJob.color }}
                          className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full"
                        />
                        <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: "rgba(255,255,255,0.55)", lineHeight: 1.75 }}>
                          {task}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  {/* Ask about this button */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6"
                  >
                    <AskAboutButton
                      subject={`${currentJob.role} @ ${currentJob.company}`}
                      language={language}
                      color={currentJob.color}
                      section="experience"
                      context={`Poste: ${currentJob.role}\nEntreprise: ${currentJob.company}\nPériode: ${currentJob.period}\nLieu: ${currentJob.location}\nType: ${currentJob.type}\nTâches:\n${(currentJob.tasks || []).map((t: string) => `- ${t}`).join("\n")}`}
                    />
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right — year label on desktop */}
            <div className="hidden lg:flex flex-col items-center flex-shrink-0 gap-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIdx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  transition={{ duration: 0.4 }}
                  className="flex flex-col items-center gap-2"
                >
                  <motion.span
                    animate={{ color: currentJob.color }}
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "56px",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {currentJob.year}
                  </motion.span>
                  <div
                    className="w-px"
                    style={{ height: "40px", background: `linear-gradient(180deg, ${currentJob.color}40, transparent)` }}
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom — horizontal timeline scrubber */}
          <div className="pb-4">
            {/* Year labels */}
            <div className="flex justify-between mb-2">
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "rgba(255,255,255,0.25)", letterSpacing: "0.1em" }}>
                {START_YEAR}
              </span>
              <div className="flex items-center gap-1.5">
                <motion.div animate={{ background: currentJob.color }} className="w-1.5 h-1.5 rounded-full" style={{ boxShadow: `0 0 6px ${currentJob.color}` }} />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={displayYear}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "rgba(255,255,255,0.5)" }}
                  >
                    {displayYear}
                  </motion.span>
                </AnimatePresence>
              </div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "rgba(255,255,255,0.25)", letterSpacing: "0.1em" }}>
                {END_YEAR}
              </span>
            </div>

            {/* Progress track */}
            <div
              className="relative h-0.5 rounded-full overflow-hidden mb-5"
              style={{ background: "rgba(255,255,255,0.07)" }}
            >
              <motion.div
                animate={{ width: `${progress * 100}%`, background: `linear-gradient(90deg, #86efac, ${currentJob.color})` }}
                transition={{ type: "spring", stiffness: 120, damping: 30 }}
                className="absolute left-0 top-0 h-full rounded-full"
                style={{ boxShadow: `0 0 8px ${currentJob.color}60` }}
              />
              {/* Job dots on the scrubber */}
              {finalDisplayJobs.map((job: any, i: number) => {
                const pos = ((i * (1 / finalDisplayJobs.length)) + 1 / (finalDisplayJobs.length * 2)) * 100;
                return (
                  <motion.div
                    key={job.id}
                    animate={{
                      background: i <= activeIdx ? job.color : "rgba(255,255,255,0.15)",
                      scale: i === activeIdx ? 1.4 : 1,
                    }}
                    transition={{ duration: 0.3 }}
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full -ml-1"
                    style={{ left: `${pos}%` }}
                  />
                );
              })}
            </div>

            {/* Scroll hint */}
            <div className="flex items-center justify-center gap-2">
              <motion.div
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              >
                <ChevronDown size={14} color="rgba(255,255,255,0.2)" />
              </motion.div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "rgba(255,255,255,0.2)", letterSpacing: "0.12em" }}>
                {progress < 0.95 ? "SCROLLEZ POUR AVANCER DANS LE TEMPS" : "FIN DU PARCOURS"}
              </span>
              {progress >= 0.95 && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#22d3ee", letterSpacing: "0.08em" }}
                >
                  ✓ PRÉSENT
                </motion.span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
